/**
 * Página de Configuración - Diferenciada por rol
 * - admin_union / admin_association: Catálogos del sistema
 * - club_director: Configuración del club
 */

import { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  SectionBadge,
} from "@/components/ui";
import {
  Settings,
  Award,
  GraduationCap,
  Users,
  DollarSign,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Database,
  Building2,
  Bell,
  Save,
  Phone,
  Clock,
  Search,
  ChevronDown,
  ChevronRight,
  Shield,
  UserCog,
} from "lucide-react";
import {
  useSpecialties,
  useProgressClasses,
  usePositions,
  useFinancialConcepts,
  useAcademicPeriods,
  useDeleteSpecialty,
  useDeleteProgressClass,
  useDeletePosition,
  useDeleteFinancialConcept,
  useSetActivePeriod,
  useSeedCatalogs,
} from "@/hooks/useCatalogs";
import { useAuthStore } from "@/stores/authStore";
import { Can } from "@/components/auth/Can";
import NotificationPreferences from "@/pages/settings/NotificationPreferences";
import { useClub, useUpdateClub } from "@/hooks/useOrganization";
import type { Specialty, ProgressClass, Position, ClubType } from "@/types";
import type { FinancialConcept, AcademicPeriod } from "@/lib/services/catalogs";
import { getActiveSections } from "@/lib/utils";
import SpecialtyModal from "./components/SpecialtyModal";
import ProgressClassModal from "./components/ProgressClassModal";
import PositionModal from "./components/PositionModal";
import FinancialConceptModal from "./components/FinancialConceptModal";
import AcademicPeriodModal from "./components/AcademicPeriodModal";
import SectionDirectorsPanel from "./components/SectionDirectorsPanel";

type TabType = "specialties" | "classes" | "positions" | "concepts" | "periods";
type ClubTabType = "general" | "notifications" | "preferences" | "directorship";

const adminTabs = [
  { id: "specialties" as TabType, label: "Especialidades", icon: Award },
  { id: "classes" as TabType, label: "Clases", icon: GraduationCap },
  { id: "positions" as TabType, label: "Cargos", icon: Users },
  { id: "concepts" as TabType, label: "Conceptos", icon: DollarSign },
  { id: "periods" as TabType, label: "Periodos", icon: Calendar },
];

const clubTabs = [
  { id: "general" as ClubTabType, label: "Datos del Club", icon: Building2 },
  { id: "notifications" as ClubTabType, label: "Notificaciones", icon: Bell },
  { id: "preferences" as ClubTabType, label: "Preferencias", icon: Settings },
  { id: "directorship" as ClubTabType, label: "Dirección", icon: UserCog },
];

const clubTypeLabels: Record<string, string> = {
  aventureros: "Aventureros",
  conquistadores: "Conquistadores",
  guias: "Guías Mayores",
  guias_mayores: "Guías Mayores",
};

export default function SettingsPage() {
  const { profile } = useAuthStore();
  const isClubDirector = profile?.primaryRole === "club_director";

  const [activeTab, setActiveTab] = useState<TabType>("specialties");
  const [clubTab, setClubTab] = useState<ClubTabType>("general");
  const [modalOpen, setModalOpen] = useState(false);
  const [specialtySearch, setSpecialtySearch] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [editingItem, setEditingItem] = useState<
    | Specialty
    | ProgressClass
    | Position
    | FinancialConcept
    | AcademicPeriod
    | null
  >(null);

  // Data queries for admin
  const { data: specialties, isLoading: loadingSpecialties } = useSpecialties();
  const { data: progressClasses, isLoading: loadingClasses } =
    useProgressClasses();
  const { data: positions, isLoading: loadingPositions } = usePositions();
  const { data: financialConcepts, isLoading: loadingConcepts } =
    useFinancialConcepts();
  const { data: academicPeriods, isLoading: loadingPeriods } =
    useAcademicPeriods();

  // Data for club director
  const clubId = profile?.directorOfClub?.id || "";
  const { data: club, isLoading: loadingClub } = useClub(clubId);
  const updateClub = useUpdateClub();

  // Club form state
  const [clubForm, setClubForm] = useState({
    meetingDay: "",
    meetingTime: "",
    meetingPlace: "",
    contactPhone: "",
    contactEmail: "",
  });
  const [savingClub, setSavingClub] = useState(false);

  // Update form when club data loads
  useState(() => {
    if (club) {
      setClubForm({
        meetingDay: club.meetingDay || "",
        meetingTime: club.meetingTime || "",
        meetingPlace: club.meetingPlace || "",
        contactPhone: club.contactPhone || "",
        contactEmail: club.contactEmail || "",
      });
    }
  });

  // Mutations
  const deleteSpecialty = useDeleteSpecialty();
  const deleteProgressClass = useDeleteProgressClass();
  const deletePosition = useDeletePosition();
  const deleteFinancialConcept = useDeleteFinancialConcept();
  const setActivePeriod = useSetActivePeriod();
  const seedCatalogs = useSeedCatalogs();

  const openModal = (
    item?:
      | Specialty
      | ProgressClass
      | Position
      | FinancialConcept
      | AcademicPeriod,
  ) => {
    setEditingItem(item || null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Estás seguro de eliminar este elemento?")) return;

    switch (activeTab) {
      case "specialties":
        await deleteSpecialty.mutateAsync(id);
        break;
      case "classes":
        await deleteProgressClass.mutateAsync(id);
        break;
      case "positions":
        await deletePosition.mutateAsync(id);
        break;
      case "concepts":
        await deleteFinancialConcept.mutateAsync(id);
        break;
    }
  };

  const handleSeedCatalogs = async () => {
    if (
      !confirm(
        "¿Inicializar catálogos con datos de ejemplo? Esto solo funciona si no hay datos existentes.",
      )
    )
      return;
    await seedCatalogs.mutateAsync();
  };

  const renderContent = () => {
    switch (activeTab) {
      case "specialties":
        return renderSpecialties();
      case "classes":
        return renderProgressClasses();
      case "positions":
        return renderPositions();
      case "concepts":
        return renderFinancialConcepts();
      case "periods":
        return renderAcademicPeriods();
      default:
        return null;
    }
  };

  const renderSpecialties = () => {
    if (loadingSpecialties) {
      return <LoadingState />;
    }

    if (!specialties?.length) {
      return <EmptyState message="No hay especialidades" />;
    }

    // Filtrar por búsqueda
    const filtered = specialtySearch
      ? specialties.filter(
          (s) =>
            s.name.toLowerCase().includes(specialtySearch.toLowerCase()) ||
            s.category.toLowerCase().includes(specialtySearch.toLowerCase()) ||
            (s.code &&
              s.code.toLowerCase().includes(specialtySearch.toLowerCase())),
        )
      : specialties;

    // Agrupar por categoría
    const byCategory = filtered.reduce(
      (acc, s) => {
        if (!acc[s.category]) acc[s.category] = [];
        acc[s.category].push(s);
        return acc;
      },
      {} as Record<string, typeof specialties>,
    );

    const categories = Object.keys(byCategory).sort();
    const toggleCategory = (category: string) => {
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        if (next.has(category)) {
          next.delete(category);
        } else {
          next.add(category);
        }
        return next;
      });
    };

    const expandAll = () => setExpandedCategories(new Set(categories));
    const collapseAll = () => setExpandedCategories(new Set());

    return (
      <div className="space-y-4">
        {/* Búsqueda y controles */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar especialidad..."
              value={specialtySearch}
              onChange={(e) => setSpecialtySearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={expandAll}
              className="text-purple-600 hover:text-purple-800 font-medium transition-colors duration-200"
            >
              Expandir todo
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={collapseAll}
              className="text-purple-600 hover:text-purple-800 font-medium transition-colors duration-200"
            >
              Colapsar todo
            </button>
          </div>
        </div>

        {/* Info de resultados */}
        <div className="text-sm text-gray-500">
          {filtered.length} especialidad{filtered.length !== 1 ? "es" : ""} en{" "}
          {categories.length} categoría{categories.length !== 1 ? "s" : ""}
          {specialtySearch && ` (filtrado de ${specialties.length})`}
        </div>

        {/* Lista de categorías colapsables */}
        <div className="space-y-3">
          {categories.map((category) => {
            const items = byCategory[category];
            const isExpanded = expandedCategories.has(category);

            return (
              <div
                key={category}
                className="border-2 border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
              >
                {/* Header de categoría */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${isExpanded ? "bg-purple-100" : "bg-gray-100"}`}
                    >
                      {isExpanded ? (
                        <ChevronDown
                          className={`w-4 h-4 ${isExpanded ? "text-purple-600" : "text-gray-500"}`}
                        />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                    <span className="font-semibold text-gray-900">
                      {category}
                    </span>
                    <Badge className="bg-purple-100 text-purple-700">
                      {items.length}
                    </Badge>
                  </div>
                </button>

                {/* Contenido expandible */}
                {isExpanded && (
                  <div className="p-4 bg-white border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {items.map((specialty) => (
                        <div
                          key={specialty.id}
                          className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-purple-200 hover:shadow-md group transition-all duration-200"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="font-medium text-sm truncate">
                                {specialty.name}
                              </span>
                              <span className="text-xs text-gray-400">
                                ({specialty.code})
                              </span>
                            </div>
                            <div className="flex gap-1 mt-1">
                              {specialty.clubTypes?.map((type) => (
                                <SectionBadge
                                  key={type}
                                  section={type as ClubType}
                                  size="xs"
                                  short
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal(specialty);
                              }}
                              className="p-1.5 hover:bg-purple-100 rounded-xl transition-colors duration-200"
                            >
                              <Edit className="w-3.5 h-3.5 text-purple-600" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(specialty.id);
                              }}
                              className="p-1.5 hover:bg-red-100 rounded-xl transition-colors duration-200"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && specialtySearch && (
          <div className="text-center py-12 text-gray-500">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Search className="w-8 h-8 text-gray-500" />
            </div>
            <p className="font-medium">
              No se encontraron especialidades para "{specialtySearch}"
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderProgressClasses = () => {
    if (loadingClasses) {
      return <LoadingState />;
    }

    if (!progressClasses?.length) {
      return <EmptyState message="No hay clases" />;
    }

    // Agrupar por tipo de club
    const byClubType = progressClasses.reduce(
      (acc, c) => {
        if (!acc[c.clubType]) acc[c.clubType] = [];
        acc[c.clubType].push(c);
        return acc;
      },
      {} as Record<string, typeof progressClasses>,
    );

    return (
      <div className="space-y-6">
        {Object.entries(byClubType).map(([clubType, items]) => (
          <div key={clubType}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-sm">
                <GraduationCap className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                {clubTypeLabels[clubType] || clubType}
              </h3>
            </div>
            <div className="space-y-2">
              {items
                .sort((a, b) => a.order - b.order)
                .map((cls) => (
                  <div
                    key={cls.id}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-primary-200 hover:shadow-md group transition-all duration-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                          {cls.order}
                        </span>
                        <span className="font-medium">{cls.name}</span>
                        <span className="text-xs text-gray-500">
                          ({cls.code})
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 ml-9">
                        Edad: {cls.minAge}
                        {cls.maxAge ? ` - ${cls.maxAge}` : "+"} años
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openModal(cls)}
                        className="p-1.5 hover:bg-purple-100 rounded-xl transition-colors duration-200"
                      >
                        <Edit className="w-4 h-4 text-purple-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(cls.id)}
                        className="p-1.5 hover:bg-red-100 rounded-xl transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPositions = () => {
    if (loadingPositions) {
      return <LoadingState />;
    }

    if (!positions?.length) {
      return <EmptyState message="No hay cargos" />;
    }

    const levelLabels: Record<string, string> = {
      club: "Club",
      unit: "Unidad/Patrulla",
      church: "Iglesia",
      district: "Distrito",
      association: "Asociación",
      union: "Unión",
    };

    // Agrupar por nivel
    const byLevel = positions.reduce(
      (acc, p) => {
        if (!acc[p.level]) acc[p.level] = [];
        acc[p.level].push(p);
        return acc;
      },
      {} as Record<string, typeof positions>,
    );

    return (
      <div className="space-y-6">
        {Object.entries(byLevel).map(([level, items]) => (
          <div key={level}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-sm">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                {levelLabels[level] || level}
              </h3>
            </div>
            <div className="space-y-2">
              {items.map((position) => (
                <div
                  key={position.id}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md group transition-all duration-200"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{position.name}</span>
                      <span className="text-xs text-gray-500">
                        ({position.code})
                      </span>
                    </div>
                    {position.clubTypes && (
                      <div className="flex gap-1 mt-1">
                        {position.clubTypes.map((type) => (
                          <SectionBadge
                            key={type}
                            section={type as ClubType}
                            size="xs"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openModal(position)}
                      className="p-1.5 hover:bg-indigo-100 rounded-xl transition-colors duration-200"
                    >
                      <Edit className="w-4 h-4 text-indigo-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(position.id)}
                      className="p-1.5 hover:bg-red-100 rounded-xl transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderFinancialConcepts = () => {
    if (loadingConcepts) {
      return <LoadingState />;
    }

    if (!financialConcepts?.length) {
      return <EmptyState message="No hay conceptos" />;
    }

    const typeLabels: Record<string, string> = {
      income: "Ingresos",
      expense: "Gastos",
    };

    // Agrupar por tipo
    const byType = financialConcepts.reduce(
      (acc, c) => {
        if (!acc[c.type]) acc[c.type] = [];
        acc[c.type].push(c);
        return acc;
      },
      {} as Record<string, typeof financialConcepts>,
    );

    return (
      <div className="space-y-6">
        {Object.entries(byType).map(([type, items]) => (
          <div key={type}>
            <div className="flex items-center gap-2 mb-3">
              <div
                className={`w-8 h-8 rounded-xl flex items-center justify-center shadow-sm ${type === "income" ? "bg-gradient-to-br from-emerald-400 to-emerald-600" : "bg-gradient-to-br from-rose-400 to-rose-600"}`}
              >
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                {typeLabels[type] || type}
              </h3>
            </div>
            <div className="space-y-2">
              {items.map((concept) => (
                <div
                  key={concept.id}
                  className={`flex items-center justify-between p-4 rounded-xl border group transition-all duration-200 hover:shadow-md ${
                    type === "income"
                      ? "bg-gradient-to-r from-emerald-50/50 to-white border-emerald-100 hover:border-emerald-200"
                      : "bg-gradient-to-r from-rose-50/50 to-white border-rose-100 hover:border-rose-200"
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{concept.name}</span>
                      <span className="text-xs text-gray-500">
                        ({concept.code})
                      </span>
                      {concept.isRecurring && (
                        <Badge className="bg-purple-100 text-purple-800">
                          Recurrente
                        </Badge>
                      )}
                    </div>
                    {concept.defaultAmount && (
                      <div className="text-xs text-gray-500 mt-1">
                        Monto por defecto: $
                        {concept.defaultAmount.toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openModal(concept)}
                      className="p-1.5 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                    >
                      <Edit className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(concept.id)}
                      className="p-1.5 hover:bg-red-100 rounded-xl transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAcademicPeriods = () => {
    if (loadingPeriods) {
      return <LoadingState />;
    }

    if (!academicPeriods?.length) {
      return <EmptyState message="No hay periodos" />;
    }

    return (
      <div className="space-y-3">
        {academicPeriods.map((period) => (
          <div
            key={period.id}
            className={`flex items-center justify-between p-4 rounded-xl border-2 group transition-all duration-200 hover:shadow-md ${
              period.isActive
                ? "bg-gradient-to-r from-primary-50 to-white border-primary-300 shadow-md shadow-primary-500/10"
                : "bg-gradient-to-r from-gray-50 to-white border-gray-100 hover:border-gray-200"
            }`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                    period.isActive
                      ? "bg-gradient-to-br from-primary-400 to-primary-600 shadow-sm shadow-primary-500/25"
                      : "bg-gray-200"
                  }`}
                >
                  <Calendar
                    className={`w-4 h-4 ${period.isActive ? "text-white" : "text-gray-500"}`}
                  />
                </div>
                <span className="font-medium">{period.name}</span>
                {period.isActive && (
                  <Badge className="bg-primary-100 text-primary-800">
                    Activo
                  </Badge>
                )}
              </div>
              <div className="text-sm text-gray-500 mt-1 ml-10">
                {new Date(period.startDate).toLocaleDateString()} -{" "}
                {new Date(period.endDate).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-2">
              {!period.isActive && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setActivePeriod.mutate(period.id)}
                  className="rounded-xl"
                >
                  Activar
                </Button>
              )}
              <button
                onClick={() => openModal(period)}
                className="p-1.5 hover:bg-gray-100 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300"
              >
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderModal = () => {
    switch (activeTab) {
      case "specialties":
        return (
          <SpecialtyModal
            isOpen={modalOpen}
            onClose={closeModal}
            specialty={editingItem as Specialty | null}
          />
        );
      case "classes":
        return (
          <ProgressClassModal
            isOpen={modalOpen}
            onClose={closeModal}
            progressClass={editingItem as ProgressClass | null}
          />
        );
      case "positions":
        return (
          <PositionModal
            isOpen={modalOpen}
            onClose={closeModal}
            position={editingItem as Position | null}
          />
        );
      case "concepts":
        return (
          <FinancialConceptModal
            isOpen={modalOpen}
            onClose={closeModal}
            concept={editingItem as FinancialConcept | null}
          />
        );
      case "periods":
        return (
          <AcademicPeriodModal
            isOpen={modalOpen}
            onClose={closeModal}
            period={editingItem as AcademicPeriod | null}
          />
        );
      default:
        return null;
    }
  };

  // ==================== RENDER CLUB SETTINGS ====================
  const handleSaveClubSettings = async () => {
    if (!clubId) return;
    setSavingClub(true);
    try {
      await updateClub.mutateAsync({
        id: clubId,
        data: {
          meetingDay: clubForm.meetingDay,
          meetingTime: clubForm.meetingTime,
          meetingPlace: clubForm.meetingPlace,
          contactPhone: clubForm.contactPhone,
          contactEmail: clubForm.contactEmail,
        },
      });
    } finally {
      setSavingClub(false);
    }
  };

  const renderClubSettings = () => {
    if (loadingClub) {
      return <LoadingState />;
    }

    if (!club) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-500 font-medium">
            No se encontró información del club
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Configuración del Club
              </h1>
              <p className="text-gray-500 mt-1">
                Administra la información y preferencias de {club.name}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-100 rounded-xl p-1.5 inline-flex gap-1">
          {clubTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setClubTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                clubTab === tab.id
                  ? "bg-white text-primary-600 shadow-md"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {clubTab === "general" && (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Info del Club */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  Información General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Nombre del Club
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {club.name}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Tipo
                  </label>
                  <div className="mt-1 flex gap-1">
                    {getActiveSections(club).map((s) => (
                      <SectionBadge key={s} section={s as ClubType} size="xs" />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Estado
                  </label>
                  <div className="mt-1">
                    <Badge
                      className={
                        club.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }
                    >
                      {club.status === "active" ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Iglesia
                  </label>
                  <p className="text-gray-900">
                    {club.churchName || "No asignada"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Reuniones */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-md shadow-amber-500/25">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  Reuniones
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Día de reunión
                  </label>
                  <select
                    value={clubForm.meetingDay}
                    onChange={(e) =>
                      setClubForm({ ...clubForm, meetingDay: e.target.value })
                    }
                    className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                  >
                    <option value="">Seleccionar día</option>
                    <option value="lunes">Lunes</option>
                    <option value="martes">Martes</option>
                    <option value="miercoles">Miércoles</option>
                    <option value="jueves">Jueves</option>
                    <option value="viernes">Viernes</option>
                    <option value="sabado">Sábado</option>
                    <option value="domingo">Domingo</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Hora de reunión
                  </label>
                  <input
                    type="time"
                    value={clubForm.meetingTime}
                    onChange={(e) =>
                      setClubForm({ ...clubForm, meetingTime: e.target.value })
                    }
                    className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Lugar de reunión
                  </label>
                  <input
                    type="text"
                    value={clubForm.meetingPlace}
                    onChange={(e) =>
                      setClubForm({ ...clubForm, meetingPlace: e.target.value })
                    }
                    placeholder="Ej: Salón social de la iglesia"
                    className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contacto */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-md shadow-green-500/25">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Teléfono de contacto
                  </label>
                  <input
                    type="tel"
                    value={clubForm.contactPhone}
                    onChange={(e) =>
                      setClubForm({ ...clubForm, contactPhone: e.target.value })
                    }
                    placeholder="Ej: +57 300 123 4567"
                    className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Email de contacto
                  </label>
                  <input
                    type="email"
                    value={clubForm.contactEmail}
                    onChange={(e) =>
                      setClubForm({ ...clubForm, contactEmail: e.target.value })
                    }
                    placeholder="Ej: club@ejemplo.com"
                    className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Guardar */}
            <Card className="md:col-span-2 border-0 shadow-lg bg-gradient-to-r from-gray-50 to-white">
              <CardContent className="py-4">
                <div className="flex justify-end">
                  <Can permission="club.write" clubId={clubId}>
                    <Button
                      onClick={handleSaveClubSettings}
                      disabled={savingClub}
                      className="rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300"
                    >
                      {savingClub ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Guardar Cambios
                    </Button>
                  </Can>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {clubTab === "notifications" && <NotificationPreferences />}

        {clubTab === "preferences" && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Preferencias Generales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-500 text-sm">
                  Configura las preferencias generales del club.
                </p>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border-2 border-gray-100 rounded-xl hover:bg-gray-50 cursor-pointer transition-all duration-300">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <div>
                      <p className="font-medium">
                        Permitir inscripciones automáticas
                      </p>
                      <p className="text-sm text-gray-500">
                        Los miembros pueden inscribirse a eventos sin aprobación
                        previa
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 border-2 border-gray-100 rounded-xl hover:bg-gray-50 cursor-pointer transition-all duration-300">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <div>
                      <p className="font-medium">
                        Mostrar información de contacto públicamente
                      </p>
                      <p className="text-sm text-gray-500">
                        Permitir que otros clubes vean la información de
                        contacto
                      </p>
                    </div>
                  </label>
                </div>
                <div className="pt-4 text-center text-sm text-gray-400">
                  Esta funcionalidad estará disponible próximamente
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {clubTab === "directorship" && (
          <SectionDirectorsPanel clubId={clubId} />
        )}
      </div>
    );
  };

  // ==================== RENDER ADMIN SETTINGS ====================
  const renderAdminSettings = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-400 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/30">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Configuración del Sistema
              </h1>
              <p className="text-gray-500 mt-1">
                Administra catálogos y ajustes del sistema
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={handleSeedCatalogs}
            disabled={seedCatalogs.isPending}
            className="rounded-xl"
          >
            {seedCatalogs.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            Inicializar Catálogos
          </Button>
        </div>

        {/* Tabs */}
        <div className="bg-gray-100 rounded-xl p-1.5 inline-flex gap-1">
          {adminTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-white text-primary-600 shadow-md"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">
              {adminTabs.find((t) => t.id === activeTab)?.label}
            </CardTitle>
            {activeTab !== "periods" && (
              <Button
                size="sm"
                onClick={() => openModal()}
                className="rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300"
              >
                <Plus className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            )}
            {activeTab === "periods" && (
              <Button
                size="sm"
                onClick={() => openModal()}
                className="rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuevo Periodo
              </Button>
            )}
          </CardHeader>
          <CardContent>{renderContent()}</CardContent>
        </Card>

        {/* Modal */}
        {renderModal()}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  if (isClubDirector) {
    return renderClubSettings();
  }

  return renderAdminSettings();
}

function LoadingState() {
  return (
    <div className="flex justify-center py-8">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12 text-gray-500">
      <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
        <Settings className="w-8 h-8 text-gray-500" />
      </div>
      <p className="font-medium text-gray-600">{message}</p>
      <p className="text-sm mt-1">Haz clic en "Agregar" para crear uno nuevo</p>
    </div>
  );
}
